import { contentPieces as demoContentPieces } from "./demoData";
import type { AssetStatus, ContentPiece } from "./types";
import {
  getClientDeckEligibility,
  stageAfterAssetReview,
  stageAfterBriefApproval,
  stageAfterDesignerAssignment,
  type CreativeAssetSnapshot,
  type CreativeBriefSnapshot,
} from "./workflowPolicy";
import { buildCreativeAssetR2Key } from "./assetStorage";

export type WorkflowNotice = {
  tone: "info" | "success" | "warning";
  message: string;
};

export type WorkflowBrief = CreativeBriefSnapshot & {
  updatedAt: number;
};

export type WorkflowAsset = CreativeAssetSnapshot & {
  id: string;
  fileName: string;
  mimeType: string;
  byteSize: number;
  source: "designer_upload" | "fal_placeholder";
  createdAt: number;
};

export type DeckReviewStatus = "not_reviewed" | "approved" | "changes_requested" | "scheduled";

export type WorkflowDeckItemSnapshot = {
  id: string;
  pieceId: string;
  title: string;
  contentType: ContentPiece["contentType"];
  dateLabel: string;
  briefBody: string;
  briefRevision: number;
  clientCaption?: string;
  reference?: string;
  publishingDestinations?: Array<"facebook" | "instagram" | "tiktok" | "whatsapp">;
  publishedAt?: number;
  clientReviewerName?: string;
  assetId: string;
  displayAssetVersionId?: string;
  assetRole: WorkflowAsset["role"];
  assetR2Key: string;
  assetFileName: string;
  displayFiles?: Array<{
    sortOrder: number;
    r2Key: string;
    thumbnailR2Key?: string;
    fileName: string;
    mimeType: string;
    byteSize: number;
    width?: number;
    height?: number;
    altText?: string;
    signedUrl?: string;
    thumbnailSignedUrl?: string;
    publicUrl?: string;
    previewUrl?: string;
    url?: string;
  }>;
  reviewStatus: DeckReviewStatus;
  clientChangeRequestUnresolvedCount?: number;
  clientChangeRequestStatus?: "open" | "resolved" | "superseded";
  invalidated?: boolean;
  order: number;
  fingerprint: string;
};

export type WorkflowDeckVersion = {
  id: string;
  label: string;
  status: "sent" | "changes_requested" | "partially_approved" | "approved";
  sentAt: number;
  reviewToken: string;
  items: WorkflowDeckItemSnapshot[];
};

export type WorkflowDeckComment = {
  id: string;
  deckVersionId: string;
  pieceId?: string;
  body: string;
  authorRole: "account_manager" | "client_reviewer";
  createdAt: number;
};

export type ContentWorkflowState = {
  pieces: ContentPiece[];
  briefs: WorkflowBrief[];
  assets: WorkflowAsset[];
  deckVersions: WorkflowDeckVersion[];
  deckComments: WorkflowDeckComment[];
  notice?: WorkflowNotice;
};

export type DeckItem = {
  piece: ContentPiece;
  brief: WorkflowBrief;
  asset: WorkflowAsset;
};

export function createInitialContentWorkflowState(): ContentWorkflowState {
  const pieces = demoContentPieces.map(clonePiece);
  return {
    pieces,
    briefs: pieces.map(createInitialBrief),
    assets: createInitialAssets(),
    deckVersions: [],
    deckComments: [],
    notice: {
      tone: "info",
      message: "Briefing, design, and client review share one monthly proposal state.",
    },
  };
}

export function draftBriefForPiece(state: ContentWorkflowState, pieceId: string): ContentWorkflowState {
  const piece = findPiece(state, pieceId);
  if (!piece) {
    return withNotice(state, "warning", "Brief could not be drafted.");
  }

  return upsertBrief(state, pieceId, {
    status: "draft",
    body: buildBriefBody(piece),
    notice: `${piece.title} brief drafted.`,
  });
}

export function reviseBriefForPiece(
  state: ContentWorkflowState,
  pieceId: string,
  revisionRequest: string,
): ContentWorkflowState {
  const piece = findPiece(state, pieceId);
  const brief = findBrief(state, pieceId);
  const request = revisionRequest.trim();

  if (!piece || !brief || !request) {
    return withNotice(state, "warning", "Add a revision request before asking AI to revise the brief.");
  }

  return upsertBrief(state, pieceId, {
    status: "draft",
    body: `${brief.body}\n\nRevision request: ${request}\nUpdated direction: Make the concept more specific, production-ready, and tied to ${piece.title}.`,
    notice: `${piece.title} brief revised.`,
  });
}

export function saveBriefEdit(state: ContentWorkflowState, pieceId: string, body: string): ContentWorkflowState {
  const piece = findPiece(state, pieceId);
  const trimmedBody = body.trim();

  if (!piece || !trimmedBody) {
    return withNotice(state, "warning", "Brief edits need body text before saving.");
  }

  return upsertBrief(state, pieceId, {
    status: "draft",
    body: trimmedBody,
    notice: `${piece.title} brief edits saved.`,
  });
}

export function markBriefReady(state: ContentWorkflowState, pieceId: string): ContentWorkflowState {
  return setBriefStatus(state, pieceId, "ready", "brief_ready", "Brief marked ready for internal approval.");
}

export function approveBrief(state: ContentWorkflowState, pieceId: string): ContentWorkflowState {
  const piece = findPiece(state, pieceId);
  const nextStage = piece ? stageAfterBriefApproval(piece.stage) : "brief_approved";
  return setBriefStatus(state, pieceId, "approved", nextStage, "Brief approved internally.");
}

export function assignDesigner(
  state: ContentWorkflowState,
  pieceId: string,
  designer: string,
): ContentWorkflowState {
  const piece = findPiece(state, pieceId);
  const brief = findBrief(state, pieceId);
  const designerName = designer.trim();

  if (!piece || brief?.status !== "approved" || !designerName) {
    return withNotice(state, "warning", "Approve the brief before assigning a designer.");
  }

  return {
    ...state,
    pieces: state.pieces.map((candidate) =>
      candidate.id === pieceId
        ? {
            ...candidate,
            designer: designerName,
            stage: stageAfterDesignerAssignment(candidate.stage),
          }
        : clonePiece(candidate),
    ),
    notice: {
      tone: "success",
      message: `${piece.title} assigned to ${designerName}.`,
    },
  };
}

export function attachDesignerAsset(
  state: ContentWorkflowState,
  pieceId: string,
  file: Pick<File, "name" | "type" | "size">,
): ContentWorkflowState {
  const piece = findPiece(state, pieceId);
  const brief = findBrief(state, pieceId);
  if (!piece || brief?.status !== "approved") {
    return withNotice(state, "warning", "Approve the brief before adding assets.");
  }

  const asset = createAsset({
    state,
    pieceId,
    source: "designer_upload",
    role: "final",
    status: "draft",
    fileName: file.name || `${piece.title}.png`,
    mimeType: file.type || "application/octet-stream",
    byteSize: file.size || 1,
  });

  return withAsset(state, pieceId, asset, "draft", `${piece.title} upload attached.`);
}

export function generatePlaceholderAsset(state: ContentWorkflowState, pieceId: string): ContentWorkflowState {
  const piece = findPiece(state, pieceId);
  const brief = findBrief(state, pieceId);
  if (!piece || brief?.status !== "approved") {
    return withNotice(state, "warning", "Approve the brief before generating placeholder assets.");
  }

  const asset = createAsset({
    state,
    pieceId,
    source: "fal_placeholder",
    role: "placeholder",
    status: "placeholder",
    fileName: `${piece.title} placeholder.png`,
    mimeType: "image/png",
    byteSize: 1024,
  });

  return withAsset(state, pieceId, asset, "placeholder", `${piece.title} placeholder generated.`);
}

export function submitAssetForReview(state: ContentWorkflowState, assetId: string): ContentWorkflowState {
  return updateAssetStatus(state, assetId, "in_review", "Asset submitted for internal review.");
}

export function approveAsset(state: ContentWorkflowState, assetId: string): ContentWorkflowState {
  return updateAssetStatus(state, assetId, "approved", "Asset approved internally.");
}

export function approveClientDeckItem(
  state: ContentWorkflowState,
  pieceId: string,
  deckVersionId?: string,
): ContentWorkflowState {
  return updateClientStatus(
    state,
    pieceId,
    "approved",
    "client_approved",
    "Client approved this content piece.",
    deckVersionId,
  );
}

export function requestClientChanges(
  state: ContentWorkflowState,
  pieceId: string,
  deckVersionId?: string,
): ContentWorkflowState {
  return updateClientStatus(
    state,
    pieceId,
    "changes_requested",
    "client_change_requested",
    "Client requested changes on this content piece.",
    deckVersionId,
  );
}

export function scheduleClientItem(
  state: ContentWorkflowState,
  pieceId: string,
  deckVersionId?: string,
): ContentWorkflowState {
  return updateClientStatus(
    state,
    pieceId,
    "scheduled",
    "in_client_deck",
    "Client scheduled this content piece.",
    deckVersionId,
  );
}

export function sendClientDeckVersion(state: ContentWorkflowState): ContentWorkflowState {
  const deckItems = getClientDeckItems(state);
  if (!deckItems.length) {
    return withNotice(state, "warning", "No accepted content is ready to send.");
  }

  const previousVersion = getActiveDeckVersion(state);
  const nextVersionIndex = state.deckVersions.length + 1;
  const snapshotItems = deckItems.map((item, index) =>
    createDeckItemSnapshot(item, index, previousVersion?.items.find((candidate) => candidate.pieceId === item.piece.id)),
  );
  const nextVersion: WorkflowDeckVersion = {
    id: `deck-v${nextVersionIndex}`,
    label: `Client review v${nextVersionIndex}`,
    status: getDeckVersionStatus(snapshotItems),
    sentAt: Date.now(),
    reviewToken: `review-deck-v${nextVersionIndex}`,
    items: snapshotItems,
  };
  const deckItemByPieceId = new Map(snapshotItems.map((item) => [item.pieceId, item]));

  return {
    ...state,
    pieces: state.pieces.map((piece) =>
      deckItemByPieceId.has(piece.id)
        ? getPieceAfterDeckSend(piece, deckItemByPieceId.get(piece.id))
        : clonePiece(piece),
    ),
    deckVersions: [...state.deckVersions.map(cloneDeckVersion), nextVersion],
    notice: {
      tone: "success",
      message: `${deckItems.length} content piece${deckItems.length === 1 ? "" : "s"} sent for client review.`,
    },
  };
}

export function getBriefForPiece(state: ContentWorkflowState, pieceId: string): WorkflowBrief | undefined {
  return findBrief(state, pieceId);
}

export function getAssetsForPiece(state: ContentWorkflowState, pieceId: string): WorkflowAsset[] {
  return state.assets.filter((asset) => asset.contentPieceId === pieceId).map(cloneAsset);
}

export function getClientDeckItems(state: ContentWorkflowState): DeckItem[] {
  return state.pieces.flatMap((piece) => {
    const brief = findBrief(state, piece.id);
    const assets = getAssetsForPiece(state, piece.id);
    const eligibility = getClientDeckEligibility({ piece, brief, assets });
    const asset = assets.find((candidate) => candidate.status === "approved");

    return eligibility.eligible && brief && asset ? [{ piece: clonePiece(piece), brief, asset }] : [];
  });
}

export function getDeckBlockedCount(state: ContentWorkflowState): number {
  return state.pieces.length - getClientDeckItems(state).length;
}

export function getActiveDeckVersion(state: ContentWorkflowState): WorkflowDeckVersion | undefined {
  const version = state.deckVersions[state.deckVersions.length - 1];
  return version ? cloneDeckVersion(version) : undefined;
}

export function getDeckVersionByReviewToken(
  state: ContentWorkflowState,
  reviewToken: string,
): WorkflowDeckVersion | undefined {
  const version = state.deckVersions.find((candidate) => candidate.reviewToken === reviewToken);
  return version ? cloneDeckVersion(version) : undefined;
}

export function addClientDeckComment(
  state: ContentWorkflowState,
  values: {
    deckVersionId: string;
    pieceId?: string;
    body: string;
    authorRole?: WorkflowDeckComment["authorRole"];
  },
): ContentWorkflowState {
  const body = values.body.trim();
  const deckVersion = state.deckVersions.find((version) => version.id === values.deckVersionId);
  const itemExists = values.pieceId
    ? deckVersion?.items.some((item) => item.pieceId === values.pieceId)
    : true;

  if (!deckVersion || !body || !itemExists) {
    return withNotice(state, "warning", "Comment could not be added to this deck version.");
  }

  return {
    ...state,
    deckComments: [
      ...state.deckComments.map(cloneDeckComment),
      {
        id: `comment-${state.deckComments.length + 1}`,
        deckVersionId: values.deckVersionId,
        pieceId: values.pieceId,
        body,
        authorRole: values.authorRole ?? "client_reviewer",
        createdAt: Date.now(),
      },
    ],
    notice: {
      tone: "success",
      message: "Comment added to the client review deck.",
    },
  };
}

function upsertBrief(
  state: ContentWorkflowState,
  pieceId: string,
  values: {
    status: WorkflowBrief["status"];
    body: string;
    notice: string;
  },
): ContentWorkflowState {
  const existing = findBrief(state, pieceId);
  const nextBrief: WorkflowBrief = {
    contentPieceId: pieceId,
    status: values.status,
    body: values.body,
    revision: (existing?.revision ?? 0) + 1,
    updatedAt: Date.now(),
  };

  return {
    ...state,
    pieces: state.pieces.map((piece) =>
      piece.id === pieceId
        ? {
            ...piece,
            stage: values.status === "ready" ? "brief_ready" : "brief_draft",
            briefSummary: values.body,
          }
        : clonePiece(piece),
    ),
    briefs: [...state.briefs.filter((brief) => brief.contentPieceId !== pieceId).map(cloneBrief), nextBrief],
    notice: {
      tone: "success",
      message: values.notice,
    },
  };
}

function setBriefStatus(
  state: ContentWorkflowState,
  pieceId: string,
  status: WorkflowBrief["status"],
  stage: ContentPiece["stage"],
  message: string,
): ContentWorkflowState {
  const piece = findPiece(state, pieceId);
  const brief = findBrief(state, pieceId);
  if (!piece || !brief) {
    return withNotice(state, "warning", "Draft the brief before changing its status.");
  }

  return {
    ...state,
    pieces: state.pieces.map((candidate) =>
      candidate.id === pieceId ? { ...candidate, stage, briefSummary: brief.body } : clonePiece(candidate),
    ),
    briefs: state.briefs.map((candidate) =>
      candidate.contentPieceId === pieceId
        ? { ...candidate, status, revision: candidate.revision + 1, updatedAt: Date.now() }
        : cloneBrief(candidate),
    ),
    notice: {
      tone: "success",
      message,
    },
  };
}

function updateAssetStatus(
  state: ContentWorkflowState,
  assetId: string,
  status: AssetStatus,
  message: string,
): ContentWorkflowState {
  const asset = state.assets.find((candidate) => candidate.id === assetId);
  if (!asset) {
    return withNotice(state, "warning", "Asset status could not be changed.");
  }

  return {
    ...state,
    pieces: state.pieces.map((piece) =>
      piece.id === asset.contentPieceId
        ? {
            ...piece,
            assetStatus: status,
            stage: stageAfterAssetReview(piece.stage, status),
          }
        : clonePiece(piece),
    ),
    assets: state.assets.map((candidate) =>
      candidate.id === assetId ? { ...cloneAsset(candidate), status } : cloneAsset(candidate),
    ),
    notice: {
      tone: "success",
      message,
    },
  };
}

function updateClientStatus(
  state: ContentWorkflowState,
  pieceId: string,
  clientStatus: ContentPiece["clientStatus"],
  stage: ContentPiece["stage"],
  message: string,
  deckVersionId?: string,
): ContentWorkflowState {
  const piece = findPiece(state, pieceId);
  if (!piece) {
    return withNotice(state, "warning", "Client review status could not be changed.");
  }

  const activeVersion = getActiveDeckVersion(state);
  const targetDeckVersionId = deckVersionId ?? activeVersion?.id;
  const targetVersion = targetDeckVersionId
    ? state.deckVersions.find((version) => version.id === targetDeckVersionId)
    : undefined;

  const eligibility = getClientDeckEligibility({
    piece,
    brief: findBrief(state, pieceId),
    assets: getAssetsForPiece(state, pieceId),
  });

  if (!targetVersion && !eligibility.eligible) {
    return withNotice(state, "warning", "Only accepted content can be reviewed by the client.");
  }

  if (targetVersion && !targetVersion.items.some((item) => item.pieceId === pieceId)) {
    return withNotice(state, "warning", "This content piece is not part of that deck version.");
  }

  const targetItem = targetVersion?.items.find((item) => item.pieceId === pieceId);
  if ((clientStatus === "approved" || clientStatus === "scheduled") && targetItem?.reviewStatus === "changes_requested") {
    return withNotice(state, "warning", "Resolve the outstanding client change request before approving or scheduling this item.");
  }

  const updatesActiveVersion = targetDeckVersionId === activeVersion?.id || !targetVersion;
  const scheduledAfterApproval = clientStatus === "scheduled"
    && (targetItem?.reviewStatus === "approved" || piece.clientStatus === "approved" || piece.stage === "client_approved");
  const nextStage = scheduledAfterApproval ? "client_approved" : stage;

  return {
    ...state,
    pieces: state.pieces.map((candidate) =>
      candidate.id === pieceId && updatesActiveVersion
        ? { ...candidate, clientStatus, stage: nextStage }
        : clonePiece(candidate),
    ),
    deckVersions: updateDeckItemStatus(
      state,
      pieceId,
      clientStatus === "approved" ? "approved" : clientStatus === "scheduled" ? "scheduled" : "changes_requested",
      targetDeckVersionId,
    ),
    notice: {
      tone: "success",
      message,
    },
  };
}

function createDeckItemSnapshot(
  item: DeckItem,
  index: number,
  previousItem?: WorkflowDeckItemSnapshot,
): WorkflowDeckItemSnapshot {
  const fingerprint = getDeckItemFingerprint(item);
  const reviewStatus =
    (previousItem?.reviewStatus === "approved" || previousItem?.reviewStatus === "scheduled") && previousItem.fingerprint === fingerprint
      ? previousItem.reviewStatus
      : "not_reviewed";

  return {
    id: `deck-item-${item.piece.id}`,
    pieceId: item.piece.id,
    title: item.piece.title,
    contentType: item.piece.contentType,
    dateLabel: item.piece.dateLabel,
    briefBody: item.brief.body,
    briefRevision: item.brief.revision,
    assetId: item.asset.id,
    assetRole: item.asset.role,
    assetR2Key: item.asset.r2Key,
    assetFileName: item.asset.fileName,
    reviewStatus,
    order: index + 1,
    fingerprint,
  };
}

function getDeckItemFingerprint(item: DeckItem): string {
  return stableLocalHash(
    [
      item.piece.id,
      item.piece.title,
      item.piece.contentType,
      item.piece.dateLabel,
      item.brief.body,
      item.brief.revision.toString(),
      item.asset.id,
      item.asset.r2Key,
      item.asset.role,
      item.asset.fileName,
    ].join("|"),
  );
}

function getDeckVersionStatus(
  items: Pick<WorkflowDeckItemSnapshot, "reviewStatus">[],
): WorkflowDeckVersion["status"] {
  if (items.every((item) => item.reviewStatus === "approved")) {
    return "approved";
  }
  if (items.some((item) => item.reviewStatus === "changes_requested")) {
    return "changes_requested";
  }
  if (items.some((item) => item.reviewStatus === "approved")) {
    return "partially_approved";
  }
  return "sent";
}

function getPieceAfterDeckSend(
  piece: ContentPiece,
  deckItem: WorkflowDeckItemSnapshot | undefined,
): ContentPiece {
  if (!deckItem) {
    return clonePiece(piece);
  }
  if (deckItem.reviewStatus === "approved") {
    return { ...piece, clientStatus: "approved", stage: "client_approved" };
  }
  if (deckItem.reviewStatus === "scheduled") {
    return {
      ...piece,
      clientStatus: "scheduled",
      stage: piece.clientStatus === "approved" || piece.stage === "client_approved" ? "client_approved" : "in_client_deck",
    };
  }
  if (piece.clientStatus === "not_sent" || piece.clientStatus === "approved" || piece.clientStatus === "scheduled") {
    return { ...piece, clientStatus: "sent", stage: "in_client_deck" };
  }
  return clonePiece(piece);
}

function updateDeckItemStatus(
  state: ContentWorkflowState,
  pieceId: string,
  reviewStatus: DeckReviewStatus,
  deckVersionId?: string,
): WorkflowDeckVersion[] {
  const activeVersion = getActiveDeckVersion(state);
  const targetDeckVersionId = deckVersionId ?? activeVersion?.id;
  if (!targetDeckVersionId) {
    return state.deckVersions.map(cloneDeckVersion);
  }

  return state.deckVersions.map((version) => {
    if (version.id !== targetDeckVersionId) {
      return cloneDeckVersion(version);
    }
    const items = version.items.map((item) =>
      item.pieceId === pieceId ? { ...cloneDeckItemSnapshot(item), reviewStatus } : cloneDeckItemSnapshot(item),
    );
    return {
      ...cloneDeckVersion(version),
      status: getDeckVersionStatus(items),
      items,
    };
  });
}

function withAsset(
  state: ContentWorkflowState,
  pieceId: string,
  asset: WorkflowAsset,
  status: AssetStatus,
  message: string,
): ContentWorkflowState {
  return {
    ...state,
    pieces: state.pieces.map((piece) =>
      piece.id === pieceId
        ? {
            ...piece,
            assetStatus: status,
            stage: stageAfterAssetReview(piece.stage, status),
          }
        : clonePiece(piece),
    ),
    assets: [...state.assets.map(cloneAsset), asset],
    notice: {
      tone: "success",
      message,
    },
  };
}

function createInitialBrief(piece: ContentPiece): WorkflowBrief {
  const status =
    piece.stage === "brief_approved" ||
    piece.stage === "assigned_to_designer" ||
    piece.stage === "asset_in_progress" ||
    piece.stage === "asset_in_review" ||
    piece.stage === "asset_approved" ||
    piece.stage === "client_change_requested" ||
    piece.stage === "client_approved" ||
    piece.stage === "in_client_deck"
      ? "approved"
      : piece.stage === "brief_ready"
        ? "ready"
        : "draft";

  return {
    contentPieceId: piece.id,
    body: piece.briefSummary,
    revision: 1,
    status,
    updatedAt: Date.now(),
  };
}

function createInitialAssets(): WorkflowAsset[] {
  return [
    {
      id: "asset-piece-001-draft",
      contentPieceId: "piece-001",
      r2Key: "content-pieces/piece-001/assets/draft/designer-upload/forgotten-item-run-draft.jpg",
      role: "draft",
      status: "in_review",
      source: "designer_upload",
      fileName: "forgotten-item-run-draft.jpg",
      mimeType: "image/jpeg",
      byteSize: 240000,
      createdAt: Date.now(),
    },
    {
      id: "asset-piece-004-final",
      contentPieceId: "piece-004",
      r2Key: "content-pieces/piece-004/assets/final/designer-upload/holiday-top-up-final.jpg",
      role: "final",
      status: "approved",
      source: "designer_upload",
      fileName: "holiday-top-up-final.jpg",
      mimeType: "image/jpeg",
      byteSize: 250000,
      createdAt: Date.now(),
    },
  ];
}

function createAsset({
  state,
  pieceId,
  source,
  role,
  status,
  fileName,
  mimeType,
  byteSize,
}: {
  state: ContentWorkflowState;
  pieceId: string;
  source: WorkflowAsset["source"];
  role: WorkflowAsset["role"];
  status: WorkflowAsset["status"];
  fileName: string;
  mimeType: string;
  byteSize: number;
}): WorkflowAsset {
  const id = `asset-${pieceId}-${state.assets.length + 1}`;
  return {
    id,
    contentPieceId: pieceId,
    r2Key: buildCreativeAssetR2Key({
      contentPieceId: pieceId,
      source,
      role,
      fileName,
      mimeType,
      byteSize,
      contentHash: stableLocalHash(`${id}:${fileName}:${byteSize}`),
    }),
    role,
    status,
    source,
    fileName,
    mimeType,
    byteSize,
    createdAt: Date.now(),
  };
}

function buildBriefBody(piece: ContentPiece): string {
  return [
    `${piece.title}`,
    "",
    `Format: ${piece.contentType}`,
    `Date: ${piece.dateLabel}`,
    "",
    "Concept:",
    piece.briefSummary,
    "",
    "Visual Direction:",
    "Show a simple, specific customer moment with the nearby market as the practical local answer.",
    "",
    "Copy Direction:",
    "Keep the caption useful and direct. Avoid generic supermarket language.",
    "",
    "Production Notes:",
    "Capture the product need, the short trip, and the resolved moment.",
  ].join("\n");
}

function findPiece(state: ContentWorkflowState, pieceId: string): ContentPiece | undefined {
  return state.pieces.find((piece) => piece.id === pieceId);
}

function findBrief(state: ContentWorkflowState, pieceId: string): WorkflowBrief | undefined {
  const brief = state.briefs.find((candidate) => candidate.contentPieceId === pieceId);
  return brief ? cloneBrief(brief) : undefined;
}

function withNotice(
  state: ContentWorkflowState,
  tone: WorkflowNotice["tone"],
  message: string,
): ContentWorkflowState {
  return {
    ...state,
    notice: { tone, message },
  };
}

function clonePiece(piece: ContentPiece): ContentPiece {
  return { ...piece };
}

function cloneBrief(brief: WorkflowBrief): WorkflowBrief {
  return { ...brief };
}

function cloneAsset(asset: WorkflowAsset): WorkflowAsset {
  return { ...asset };
}

function cloneDeckVersion(deckVersion: WorkflowDeckVersion): WorkflowDeckVersion {
  return {
    ...deckVersion,
    items: deckVersion.items.map(cloneDeckItemSnapshot),
  };
}

function cloneDeckItemSnapshot(item: WorkflowDeckItemSnapshot): WorkflowDeckItemSnapshot {
  return { ...item };
}

function cloneDeckComment(comment: WorkflowDeckComment): WorkflowDeckComment {
  return { ...comment };
}

function stableLocalHash(value: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(16, "0");
}
